import { useLazyQuery } from '@apollo/client';
import { SearchAddresses_searchAddresses_suggestions as AddressSuggestion } from '@generated/types';
import { createStyles, makeStyles, MenuItem, MenuList, Paper } from '@material-ui/core';
import { SearchAddresses } from '@services/candidates/queries';
import React, { useEffect, useRef, useState } from 'react';

import TextField, { TextFieldProps } from './TextField';

interface StreetAddressTextFieldProps extends TextFieldProps {
  onAddressSelect?: (suggestion: AddressSuggestion) => void;
  selectedCountry?: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      position: 'relative',
      width: '100%',
    },
    menuContainer: {
      position: 'absolute',
      zIndex: 13000,
      width: '100%',
      maxHeight: '200px',
      overflowY: 'auto',
      marginTop: '4px',
    },
    menuItem: {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
    },
  }),
);

const StreetAddressTextField: React.FC<StreetAddressTextFieldProps> = ({
  onAddressSelect,
  onChange,
  value,
  selectedCountry,
  ...textFieldProps
}) => {
  const classes = useStyles();
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [searchAddressesQuery] = useLazyQuery(SearchAddresses, {
    onCompleted: (data) => {
      if (data?.searchAddresses?.suggestions) {
        setAddressSuggestions(data.searchAddresses.suggestions);
        setShowSuggestions(data.searchAddresses.suggestions.length > 0);
      }
    },
    onError: () => {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    },
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  const handleChange = (newValue: string) => {
    if (onChange) {
      onChange(newValue);
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    if (newValue.trim().length >= 3) {
      const timer = setTimeout(() => {
        searchAddressesQuery({
          variables: { query: newValue.trim(), country_code: selectedCountry ?? 'US' },
        });
      }, 500);

      setDebounceTimer(timer);
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    setShowSuggestions(false);
    if (onAddressSelect) {
      onAddressSelect(suggestion);
    }
  };

  return (
    <div ref={containerRef} className={classes.container}>
      <TextField {...textFieldProps} value={value} onChange={handleChange} />
      {showSuggestions && addressSuggestions.length > 0 && (
        <Paper className={classes.menuContainer}>
          <MenuList>
            {addressSuggestions.map((suggestion) => (
              <MenuItem
                key={suggestion.street}
                onClick={() => handleSuggestionSelect(suggestion)}
                className={classes.menuItem}
              >
                {suggestion.place_name}
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      )}
    </div>
  );
};

export default StreetAddressTextField;
